import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2 } from "lucide-react";
import { getVoiceClipUrl, type VoiceClipKey } from "@/lib/lrDirect/voiceExampleClips";

interface VoiceExampleClipButtonProps {
  clip: VoiceClipKey;
  label?: string;
  /** When this flips true (e.g. recording starts), playback stops immediately. */
  stopSignal?: boolean;
}

/**
 * Small, self-contained "hear an example" player.
 * Additive UI only — no interaction with the voice engine, recorder, or posting.
 * Failures (missing clip, blocked autoplay) are swallowed: the button simply
 * returns to its idle state.
 */
export function VoiceExampleClipButton({
  clip,
  label = "Hear an example",
  stopSignal,
}: VoiceExampleClipButtonProps) {
  const url = getVoiceClipUrl(clip);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  // Stop when the parent signals (e.g. user starts recording) and on unmount.
  useEffect(() => {
    if (stopSignal && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  }, [stopSignal]);

  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, []);

  if (!url) return null;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      a.currentTime = 0;
      setPlaying(false);
      return;
    }
    a.play().catch(() => setPlaying(false));
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Stop example" : label}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
      >
        {playing ? (
          <Pause className="h-3 w-3 text-brand-teal-deep" />
        ) : (
          <Play className="h-3 w-3 text-brand-teal-deep" />
        )}
        {playing ? "Stop example" : label}
        <Volume2 className="h-3 w-3 text-muted-foreground" />
      </button>
      <audio
        ref={audioRef}
        src={url}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => setPlaying(false)}
        className="hidden"
      />
    </>
  );
}
